import { useState } from "react";
import axios from "axios";
import { ENQUIRY } from "@/constants/testIds";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  "Joining an event",
  "Corporate wellness session",
  "Tech / AI event",
  "Business / leadership event",
  "Creator / community event",
  "Hosting an event",
  "Sponsorship / partnership",
  "Custom private event",
];
const CATEGORIES = [
  "Lifestyle & Wellness",
  "Technology & AI",
  "Business & Leadership",
  "Creativity & Culture",
  "Learning & Skills",
  "Community & Networking",
  "Not sure yet",
];
const FORMATS = ["Online", "In-person", "Hybrid", "Not sure"];
const AUDIENCE = ["Individual", "2–10", "11–50", "51–200", "200+"];

export default function EnquiryForm() {
  const [f, setF] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
    role: "",
    city: "",
    interest: "",
    category: "",
    format: "",
    audience_size: "",
    message: "",
    consent: false,
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k) => (e) =>
    setF((s) => ({
      ...s,
      [k]: e && e.target ? (e.target.type === "checkbox" ? e.target.checked : e.target.value) : e,
    }));

  const validate = () => {
    const e = {};
    if (!f.full_name.trim()) e.full_name = "Full name is required";
    if (!f.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email))
      e.email = "Enter a valid email";
    if (!f.consent) e.consent = "Please confirm consent to continue";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/enquiry`, f);
      setSuccess(true);
      toast.success("Thank you.", {
        description:
          "Aura Events has received your interest. We'll contact you with relevant event or collaboration details.",
      });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="enquiry" className="py-20 md:py-28 bg-[var(--aura-ivory-2)]">
      <div className="max-w-6xl mx-auto px-6 md:px-10 lg:px-14">
        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <span className="eyebrow">Register · Enquire</span>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-3 leading-[1.05]">
              Register interest or plan an Aura event
            </h2>
            <p className="mt-5 font-body text-[var(--aura-charcoal-2)] leading-relaxed">
              Individual, team, or partner — share what you have in mind and
              we&apos;ll respond with relevant next steps.
            </p>
          </div>

          <div className="lg:col-span-8">
            {success ? (
              <div
                data-testid={ENQUIRY.success}
                className="rounded-2xl bg-white border border-black/5 p-8 md:p-10"
              >
                <div className="font-display text-2xl">Thank you.</div>
                <p className="mt-2 font-body text-[var(--aura-charcoal-2)]">
                  Aura Events has received your interest. We&apos;ll contact you with
                  relevant event or collaboration details.
                </p>
                <button
                  onClick={() => {
                    setSuccess(false);
                    setF({
                      full_name: "",
                      email: "",
                      phone: "",
                      company: "",
                      role: "",
                      city: "",
                      interest: "",
                      category: "",
                      format: "",
                      audience_size: "",
                      message: "",
                      consent: false,
                    });
                  }}
                  className="mt-6 font-ui text-sm underline underline-offset-4 text-[var(--aura-teal)]"
                >
                  Submit another enquiry
                </button>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid={ENQUIRY.form}
                noValidate
                className="rounded-2xl bg-white border border-black/5 p-6 md:p-8 grid sm:grid-cols-2 gap-5"
              >
                <Field
                  label="Full name"
                  required
                  testId={ENQUIRY.fullName}
                  value={f.full_name}
                  onChange={set("full_name")}
                  error={errors.full_name}
                />
                <Field
                  label="Email"
                  type="email"
                  required
                  testId={ENQUIRY.email}
                  value={f.email}
                  onChange={set("email")}
                  error={errors.email}
                />
                <Field
                  label="Phone / WhatsApp"
                  testId={ENQUIRY.phone}
                  value={f.phone}
                  onChange={set("phone")}
                />
                <Field
                  label="Company / organization"
                  testId={ENQUIRY.company}
                  value={f.company}
                  onChange={set("company")}
                />
                <Field
                  label="Role / designation"
                  testId={ENQUIRY.role}
                  value={f.role}
                  onChange={set("role")}
                />
                <Field
                  label="City"
                  testId={ENQUIRY.city}
                  value={f.city}
                  onChange={set("city")}
                />

                <Select
                  label="I am interested in"
                  testId={ENQUIRY.interest}
                  value={f.interest}
                  onChange={set("interest")}
                  options={INTERESTS}
                />
                <Select
                  label="Preferred event category"
                  testId={ENQUIRY.category}
                  value={f.category}
                  onChange={set("category")}
                  options={CATEGORIES}
                />
                <Select
                  label="Preferred format"
                  testId={ENQUIRY.format}
                  value={f.format}
                  onChange={set("format")}
                  options={FORMATS}
                />
                <Select
                  label="Audience size"
                  testId={ENQUIRY.audienceSize}
                  value={f.audience_size}
                  onChange={set("audience_size")}
                  options={AUDIENCE}
                />

                <label className="sm:col-span-2 block">
                  <div className="font-ui text-xs uppercase tracking-[0.18em] text-[var(--aura-muted)]">
                    Message / event idea / goals
                  </div>
                  <textarea
                    rows={4}
                    value={f.message}
                    onChange={set("message")}
                    data-testid={ENQUIRY.message}
                    className="mt-2 w-full rounded-xl border border-black/10 bg-[var(--aura-ivory)] focus:border-[var(--aura-teal)] outline-none px-4 py-3 font-body text-sm resize-none"
                  />
                </label>

                <label className="sm:col-span-2 flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={f.consent}
                    onChange={set("consent")}
                    data-testid={ENQUIRY.consent}
                    className="mt-1 accent-[var(--aura-teal)] w-4 h-4"
                  />
                  <span className="font-body text-sm text-[var(--aura-charcoal-2)]">
                    I agree to be contacted by Aura Events about upcoming
                    events, collaborations, and programs.
                  </span>
                </label>
                {errors.consent && (
                  <div className="sm:col-span-2 font-body text-xs text-[var(--aura-coral)] -mt-2">
                    {errors.consent}
                  </div>
                )}

                <div className="sm:col-span-2 flex flex-col sm:flex-row sm:items-center gap-3 mt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid={ENQUIRY.submit}
                    className="font-ui text-sm px-6 py-3 rounded-full bg-[var(--aura-teal)] text-[var(--aura-ivory)] hover:bg-[var(--aura-teal-hover)] transition-colors disabled:opacity-60"
                  >
                    {submitting ? "Sending…" : "Submit Enquiry"}
                  </button>
                  <span className="font-body text-xs text-[var(--aura-muted)]">
                    We typically respond within a few working days.
                  </span>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, type = "text", testId, value, onChange, required, error }) {
  return (
    <label className="block">
      <div className="font-ui text-xs uppercase tracking-[0.18em] text-[var(--aura-muted)]">
        {label} {required && <span className="text-[var(--aura-coral)]">*</span>}
      </div>
      <input
        type={type}
        value={value}
        onChange={onChange}
        data-testid={testId}
        className={`mt-2 w-full rounded-xl border ${error ? "border-[var(--aura-coral)]" : "border-black/10"} bg-[var(--aura-ivory)] focus:border-[var(--aura-teal)] outline-none px-4 py-2.5 font-body text-sm`}
      />
      {error && (
        <div className="mt-1.5 font-body text-xs text-[var(--aura-coral)]">
          {error}
        </div>
      )}
    </label>
  );
}

function Select({ label, testId, value, onChange, options }) {
  return (
    <label className="block">
      <div className="font-ui text-xs uppercase tracking-[0.18em] text-[var(--aura-muted)]">
        {label}
      </div>
      <select
        value={value}
        onChange={onChange}
        data-testid={testId}
        className="mt-2 w-full rounded-xl border border-black/10 bg-[var(--aura-ivory)] focus:border-[var(--aura-teal)] outline-none px-4 py-2.5 font-body text-sm"
      >
        <option value="">Select…</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </label>
  );
}
